import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The hint for a write that waits on the infrastructure (n8n, WiService) before answering. Those can
 * legitimately take up to a minute, and a veil with no word about it reads as a frozen console long
 * before then.
 */
export const PROVIDER_WAIT_HINT =
  "This can take up to a minute. Keep this tab open.";

interface BusyOverlayProps {
  open: boolean;
  /** One short line naming what is being saved. */
  label: string;
  /** Optional second line — how long it may take, or what not to do meanwhile. */
  hint?: string;
  /**
   * Covers only the nearest positioned ancestor — the list screen's `relative` Card — instead of the
   * whole page. For a write started from a table row: the table is what must not take a second
   * write, while the sidebar and the rest of the console stay usable.
   */
  contained?: boolean;
}

/**
 * Covers the page — or, with `contained`, the table's Card — while a write is in flight, so nothing
 * behind it can be clicked until the result is on screen. The admin counterpart of the client
 * console's `BusyOverlay`: same layering, same portal and pointer-events rules, and those must stay
 * in step. Four differences are deliberate. This one has the optional `hint` line, because operator
 * writes reach the provider and can run for a minute. It has the `contained` table variant, because
 * the admin list screens start writes from a row. It fades in through a CSS keyframe with a 120ms
 * delay, because admin has no framer-motion. And for the same reason it has no fade-out — it
 * unmounts the moment the write settles.
 *
 * Page variant — portalled to `body`: rendered in place, `position: fixed` resolves against the
 * nearest transformed ancestor rather than the viewport, and a tab panel or animated card is enough
 * to shrink it to that box. `z-60` sits above the dialogs (`z-50`), so a form dialog that is saving
 * is covered too, while sonner's toasts stay above it and still report the outcome.
 *
 * Contained variant — rendered in place as `absolute inset-0` inside the list screen's `relative`
 * Card, at `z-40` so it sits over the card's own "Updating…" pill (`z-20`) and over the table's
 * sticky header (`z-30`) — at an equal z-index an overlay rendered before the table, as the orders
 * and balances screens do, leaves the header uncovered. The dialog that started the write stays
 * above it and keeps its own spinner.
 *
 * `pointer-events-auto` is load-bearing. An open Radix dialog sets `pointer-events: none` on
 * `body` and re-enables it only on its own layers, so an overlay portalled next to it inherits
 * `none` and every click passes straight through to the form it is meant to be covering.
 *
 * Fades in after a short delay (`animate-busy-overlay-in`, index.css) rather than appearing: a
 * write that answers in a few hundred milliseconds would otherwise read as the screen flickering.
 * It blocks clicks from the first frame all the same.
 *
 * Reserved for writes that must not interleave — money, status, deletes, passwords, addresses,
 * recovery, configuration. A quick in-place action reports from its own control instead.
 */
export function BusyOverlay({ open, label, hint, contained = false }: BusyOverlayProps) {
  if (!open) return null;

  const overlay = (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        "pointer-events-auto flex cursor-wait animate-busy-overlay-in items-center justify-center bg-canvas/40 backdrop-blur-[2px]",
        contained ? "absolute inset-0 z-40 rounded-[inherit]" : "fixed inset-0 z-60",
      )}
    >
      <div className="flex max-w-sm items-start gap-2.5 rounded-lg border bg-background px-4 py-2.5 shadow-lg">
        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-brand" />
        <div className="space-y-0.5">
          <span className="block text-sm text-foreground">{label}</span>
          {hint && (
            <span className="block text-xs text-muted-foreground">{hint}</span>
          )}
        </div>
      </div>
    </div>
  );

  return contained ? overlay : createPortal(overlay, document.body);
}
